import React, { useEffect, useState } from "react";
import { useLang } from "@/i18n/LanguageContext";

// Each card can have one or many images. Multiple images = automatic crossfade.
const IMAGES = [
  // 1. Harmony & Privacy — harmony_01 → harmony_02 → harmony_03
  [
    "/experiences/harmony-1.jpg",
    "/experiences/harmony-2.jpg?v=2",
    "/experiences/harmony-3.jpg",
  ],
  // 2. Nature & Sea — sea_03 first, then existing pair
  ["/sea/sea-3.jpg", "/sea/sea-1.jpg", "/sea/sea-2.jpg"],
  // 3. Island Exploration — explore_1 → activity_3 → explore_3
  [
    "/experiences/explore-1.jpg",
    "/experiences/activity-3.jpg",
    "/experiences/explore-3.jpg",
  ],
  // 4. Culinary Rituals — peka fireplace + food_2 + food_3
  [
    "https://customer-assets.emergentagent.com/job_villa-sucuraj/artifacts/7zjro6qe_IMG_9466.jpeg",
    "/experiences/food-2.jpg",
    "/experiences/food-3.jpg",
  ],
];

const CROSSFADE_INTERVAL = 5000; // ms
const CROSSFADE_DURATION = 1500; // ms — matches `duration-[1500ms]` below

const CrossfadeImage = ({ sources, alt, testId }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (sources.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % sources.length);
    }, CROSSFADE_INTERVAL);
    return () => clearInterval(id);
  }, [sources.length]);

  return (
    <div className="absolute inset-0 overflow-hidden" data-testid={testId}>
      {sources.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          loading={i === 0 ? "eager" : "lazy"}
        />
      ))}
    </div>
  );
};

export const Experiences = () => {
  const { t } = useLang();
  const items = t.experiences.items;

  return (
    <section
      id="experiences"
      data-testid="experiences-section"
      className="relative bg-[#EAE4D9] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.28em] text-[#8A9A86] font-semibold">
            {t.experiences.eyebrow}
          </span>
          <h2
            data-testid="experiences-title"
            className="mt-6 font-serif font-light text-[#2C302E] text-4xl md:text-5xl leading-[1.08] tracking-tight"
          >
            {t.experiences.title}
          </h2>
          <p className="mt-6 text-[#5C625E] text-base md:text-lg leading-relaxed font-light">
            {t.experiences.subtitle}
          </p>
        </div>

        <div className="mt-20 space-y-24 md:space-y-32">
          {items.map((item, i) => {
            const reversed = i % 2 === 1;
            const sources = IMAGES[i];
            return (
              <article
                key={i}
                data-testid={`experience-card-${i}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
              >
                <div
                  className={`md:col-span-7 ${reversed ? "md:order-2" : ""} relative`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#2C302E]/5">
                    <CrossfadeImage
                      sources={sources}
                      alt={item.title}
                      testId={`experience-image-${i}`}
                    />
                  </div>
                  <div className="absolute -top-4 -left-4 md:-top-6 md:-left-6 bg-[#C05C3D] text-[#F8F5F0] px-4 py-2 text-xs uppercase tracking-[0.24em] font-semibold z-10">
                    0{i + 1}
                  </div>
                </div>

                <div
                  className={`md:col-span-5 ${reversed ? "md:order-1 md:pr-8" : "md:pl-8"}`}
                >
                  <span className="text-xs uppercase tracking-[0.28em] text-[#1D4B69] font-semibold">
                    {item.tag}
                  </span>
                  <h3 className="mt-4 font-serif font-light text-[#2C302E] text-3xl md:text-4xl leading-[1.1] tracking-tight">
                    {item.title}
                  </h3>
                  <div className="mt-6 space-y-4 text-[#5C625E] text-base leading-relaxed font-light">
                    {item.body.split("\n\n").map((para, idx) => (
                      <p key={idx}>{para}</p>
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
